import {
  BRIDGE_SEATS,
  type BridgeCard,
  type BridgeSeat,
  type BridgeTrump,
  type BridgeView,
  cardId,
  sideOf,
  sortHand,
  viewFor,
} from '@strategy4kids/game-engine';
import { CardBack, PlayingCard, SUIT_SYMBOL, CARD_SLOT_CLASS, HAND_CLASS } from '../components/PlayingCard.js';
import { CardTable } from '../components/table/CardTable.js';
import { RoundSummaryPanel, type RoundSummary } from '../components/table/RoundSummaryPanel.js';
import { SeatPlate } from '../components/table/SeatPlate.js';
import { TrickArea } from '../components/table/TrickArea.js';
import { useSeatIdentity } from '../components/table/useSeatIdentity.js';
import { useT } from '../i18n/useT.js';
import { HUMAN_SEAT, useBridgeStore } from '../state/bridge-store.js';
import { useSeatBubbles } from '../state/table-chatter.js';

const TRUMP_CHOICES: readonly BridgeTrump[] = ['S', 'H', 'D', 'C', 'NT'];

/**
 * The Mini-Bridge screen: you in South, a robot partner in North, robot opponents East and West. It
 * renders only from `viewFor(game, HUMAN_SEAT)`, so a hand you may not see is never in the data this
 * component can reach. Rules and the teaching simplifications: docs/proposals/mindgames-and-bridge.md.
 */
export function BridgeScreen({ onExit }: { onExit: () => void }) {
  const { t } = useT('games');
  const game = useBridgeStore((s) => s.game);
  const result = useBridgeStore((s) => s.result);
  const seed = useBridgeStore((s) => s.seed);
  const strength = useBridgeStore((s) => s.strength);
  const showPoints = useBridgeStore((s) => s.showPoints);
  const fourColour = useBridgeStore((s) => s.fourColour);
  const pickTrump = useBridgeStore((s) => s.pickTrump);
  const play = useBridgeStore((s) => s.play);
  const redeal = useBridgeStore((s) => s.redeal);
  const setStrength = useBridgeStore((s) => s.setStrength);
  const setShowPoints = useBridgeStore((s) => s.setShowPoints);
  const setFourColour = useBridgeStore((s) => s.setFourColour);

  const identify = useSeatIdentity(BRIDGE_SEATS, (x) => t(`bridge-seat-${x}`));
  const bubbles = useSeatBubbles();
  if (!game) return null;
  const view = viewFor(game, HUMAN_SEAT);
  const seatName = (s: BridgeSeat) => t(`bridge-seat-${s}`);
  const trumpName = (x: BridgeTrump) => t(`bridge-suit-${x}`);
  const legal = new Set(view.legalPlays.map(cardId));
  const { contract } = view;
  const humanDeclares = contract.declarer === HUMAN_SEAT;
  const choosing = view.phase === 'trump';
  const mySide = sideOf(HUMAN_SEAT);

  let status: string;
  if (view.phase === 'done') status = result?.made ? t('bridge-made', { n: result.declarerTricks }) : t('bridge-failed', { n: result?.declarerTricks ?? 0, need: contract.tricksNeeded });
  else if (choosing) status = humanDeclares ? t('bridge-trump-prompt', { trump: trumpName(contract.suggestedTrump ?? 'NT') }) : t('bridge-trump-wait', { seat: seatName(contract.declarer) });
  else if (view.canAct) status = view.turn === HUMAN_SEAT ? t('bridge-turn-you') : t('bridge-turn-dummy', { seat: seatName(view.turn) });
  else status = t('bridge-turn-wait', { seat: seatName(view.turn) });

  const handFor = (seat: BridgeSeat): readonly BridgeCard[] | null => {
    if (seat === HUMAN_SEAT) return view.hand;
    if (view.dummy?.seat === seat) return view.dummy.hand;
    return null;
  };

  const lastTrick = view.tricks[view.tricks.length - 1];
  const shown = view.trick.length > 0 ? view.trick : (lastTrick?.plays ?? []);
  const winner = view.trick.length === 0 ? lastTrick?.winner : undefined;

  const seatHand = (seat: BridgeSeat) => {
    const cards = handFor(seat);
    return (
      <section aria-label={t('bridge-hand-of', { seat: seatName(seat) })} data-testid={`bridge-hand-${seat}`} className="flex flex-col items-center gap-1">
        <SeatPlate
          bubble={bubbles[seat]?.line}
          testid={`bridge-seat-${seat}`}
          name={`${identify(seat).name}${contract.declarer === seat ? ' ★' : ''}`}
          tag={identify(seat).tag}
          portrait={identify(seat).portrait}
          fallbackIcon={identify(seat).fallbackIcon}
          you={identify(seat).you}
          cardsLeft={cards ? undefined : view.handSizes[seat]}
          cardsLeftLabel={t('bridge-cards-left', { n: view.handSizes[seat] })}
          active={view.turn === seat && !result}
        />
        {cards ? (
          <div className={HAND_CLASS}>
            {sortHand(cards).map((c) => (
              <PlayingCard key={cardId(c)} card={c} fourColour={fourColour} onPlay={legal.has(cardId(c)) ? () => play(c) : undefined} dimmed={view.canAct && !legal.has(cardId(c))} />
            ))}
          </div>
        ) : (
          null
        )}
      </section>
    );
  };

  const declSide = sideOf(contract.declarer);
  const theirSide = mySide === 'NS' ? 'EW' : 'NS';
  const summary: RoundSummary | null = result
    ? {
        round: 1,
        outcome: result.made === (declSide === mySide) ? 'win' : 'lose',
        headline: `${'★'.repeat(result.stars) || '☆'} ${result.made ? t('bridge-made', { n: result.declarerTricks }) : t('bridge-failed', { n: result.declarerTricks, need: result.tricksNeeded })}`,
        rows: [
          { key: 'us', label: `${identify('S').name} + ${identify('N').name}`, portrait: identify('N').portrait, delta: declSide === mySide ? `★ ${result.stars}` : '', total: view.tricksWon[mySide], you: true, leading: view.tricksWon[mySide] > view.tricksWon[theirSide] },
          { key: 'them', label: `${identify('W').name} + ${identify('E').name}`, portrait: identify('W').portrait, delta: declSide === theirSide ? `★ ${result.stars}` : '', total: view.tricksWon[theirSide], leading: view.tricksWon[theirSide] > view.tricksWon[mySide] },
        ],
      }
    : null;

  const slot = (seat: BridgeSeat) => {
    const p = shown.find((x) => x.seat === seat);
    return p ? <PlayingCard compact card={p.card} fourColour={fourColour} highlight={winner === seat} /> : <span className={CARD_SLOT_CLASS} aria-hidden="true" />;
  };

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 lg:max-w-6xl lg:gap-6 lg:p-8 xl:max-w-7xl 2xl:max-w-[96rem]" data-testid="bridge-screen" data-appdom-scope="bridge">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-extrabold text-berry">{t('bridge-title')}</h1>
        <div className="flex items-center gap-2 text-sm font-bold">
          <span className="text-fg-muted" data-testid="bridge-seed">{t('bridge-deal-seed', { seed })}</span>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={redeal} data-testid="bridge-new-deal" data-appdom-id="bridge-new-deal" data-appdom-type="Button">{t('bridge-new-deal')}</button>
          <button type="button" className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2" onClick={onExit} data-testid="bridge-exit" data-appdom-id="bridge-exit" data-appdom-type="Button">{t('bridge-exit')}</button>
        </div>
      </header>

      <output className="min-h-[48px] glass glass-light flex flex-col gap-1 rounded-xl px-3 py-2 text-sm font-bold text-ink" data-testid="bridge-status" data-appdom-id="bridge-status" data-appdom-type="Text" aria-live="polite">
        <span>{status}</span>
        <span className="font-normal text-fg-muted" data-testid="bridge-contract">
          {t('bridge-contract', { side: t(`bridge-side-${contract.declaringSide}`), n: contract.tricksNeeded })} · {(contract.declarer === HUMAN_SEAT ? t('bridge-declarer-you') : t('bridge-declarer', { seat: seatName(contract.declarer) }))} ·{' '}
          {contract.trump ? t('bridge-trump-label', { trump: `${contract.trump === 'NT' ? '' : `${SUIT_SYMBOL[contract.trump]} `}${trumpName(contract.trump)}` }) : t('bridge-trump-undecided')}
        </span>
        <span className="font-normal text-fg-muted" data-testid="bridge-tricks">
          {t('bridge-tricks', { us: view.tricksWon[mySide], them: view.tricksWon[mySide === 'NS' ? 'EW' : 'NS'] })}
        </span>
        {showPoints && <PointsLine view={view} />}
      </output>

      {choosing && humanDeclares && (
        // biome-ignore lint/a11y/useSemanticElements: a flex row of buttons; a fieldset would bring its own box styling
        <div role="group" aria-label={t('bridge-trump-prompt', { trump: trumpName(contract.suggestedTrump ?? 'NT') })} className="flex flex-wrap gap-2" data-testid="bridge-trump-choices">
          {TRUMP_CHOICES.map((x) => (
            <button
              key={x}
              type="button"
              data-testid={`bridge-trump-${x}`}
              aria-pressed={contract.suggestedTrump === x}
              onClick={() => pickTrump(x)}
              className={`rounded-xl border px-4 py-2 font-display text-base font-semibold ${contract.suggestedTrump === x ? 'border-accent bg-accent text-accent-fg' : 'border-border bg-surface-raised'}`}
            >
              {x === 'NT' ? trumpName(x) : `${SUIT_SYMBOL[x]} ${trumpName(x)}`}
            </button>
          ))}
        </div>
      )}

      <CardTable
        top={seatHand('N')}
        left={seatHand('W')}
        right={seatHand('E')}
        center={<TrickArea testid="bridge-trick" label={t('bridge-trick-area')} north={slot('N')} west={slot('W')} east={slot('E')} south={slot('S')} />}
        bottom={seatHand('S')}
      />

      {summary && (
        <RoundSummaryPanel testid="bridge-result" summary={summary} nextLabel={t('bridge-new-deal')} onNext={redeal} nextTestid="bridge-play-again" />
      )}

      <details className="rounded-2xl border border-border bg-surface p-3" data-testid="bridge-settings">
        <summary className="flex min-h-[48px] cursor-pointer items-center text-sm font-extrabold uppercase tracking-wide text-fg-muted">
          {t('bridge-robots')}
        </summary>
        <div className="mt-3 flex flex-col gap-2 text-sm font-bold">
          <div className="flex items-center gap-2">
            {(['easy', 'clever'] as const).map((s) => (
              <button key={s} type="button" aria-pressed={strength === s} data-testid={`bridge-strength-${s}`} onClick={() => setStrength(s)} className={`min-h-[48px] rounded-lg border px-3 py-1 ${strength === s ? 'border-accent bg-accent text-accent-fg' : 'border-border'}`}>
                {t(`bridge-${s}`)}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={showPoints} onChange={(e) => setShowPoints(e.target.checked)} data-testid="bridge-show-points" />
            {t('bridge-show-points')}
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={fourColour} onChange={(e) => setFourColour(e.target.checked)} data-testid="bridge-four-colour" />
            {t('bridge-four-colour')}
          </label>
          <p className="font-normal text-fg-muted">{t('bridge-teaching-note')}</p>
        </div>
      </details>
    </div>
  );
}

function PointsLine({ view }: { view: BridgeView }) {
  const { t } = useT('games');
  const text =
    view.points.dummy === null
      ? t('bridge-points-own', { own: view.points.own })
      : t('bridge-points-both', { own: view.points.own, dummy: view.points.dummy });
  return (
    <span className="font-normal text-fg-muted" data-testid="bridge-points">
      {text}
    </span>
  );
}
